<template>
	<FormKit v-model="customer" type="form" :config :actions="false">
		<div class="max-w-2xl space-y-5">
			<FormKit type="group" name="personDetails">
				<DashboardCardCollapsible
					heading="Persoonsgegevens (zoals vermeld op het paspoort)"
				>
					<fieldset class="flex flex-col gap-2.5">
						<div class="flex gap-1">
							<FormKit label="Voornamen" name="firstName" />
							<FormKit
								label="Tussenvoegsel"
								name="infix"
								outer-class="max-w-[8rem]"
							/>
							<FormKit label="Achternaam" name="lastName" />
						</div>

						<div class="flex gap-8">
							<FormKit
								label="Voorletters"
								name="initials"
								outer-class="max-w-[8rem]"
								:placeholder="intitials"
							/>
							<FormKit
								type="loanlinkOption"
								name="gender"
								label="Geslacht"
								:options="genderOptions"
							/>
						</div>
						<FormKit
							type="loanlinkDate"
							name="birthDate"
							label="Geboortedatum"
							two-digit-year-century="past"
						/>
					</fieldset>
				</DashboardCardCollapsible>
			</FormKit>

			<FormKit type="group" name="contactDetails">
				<DashboardCardCollapsible heading="Contactgegevens">
					<fieldset class="grid gap-2.5 xl:grid-cols-2">
						<FormKit name="email" label="Emailadres" type="email" />
						<FormKit name="tel" label="Telefoonnummer" type="tel" />
					</fieldset>
				</DashboardCardCollapsible>
			</FormKit>

			<FormKit type="group" name="residence">
				<DashboardCardCollapsible heading="Huidig woonadres">
					<AppButton
						v-if="canImportResidence"
						variant="primary"
						class="self-start mb-8"
						type="button"
						@click="importResidence"
					>
						{{ residenceImportText }}
					</AppButton>

					<fieldset class="grid gap-2.5 xl:grid-cols-3">
						<FormKit name="postcode" label="Postcode" />
						<FormKit name="housenumber" label="Huisnummer" />
					</fieldset>
					<FormKit name="streetName" label="Straat" />
					<FormKit name="place" label="Plaats" />
					<FormKit
						name="country"
						type="select"
						select-icon="down"
						label="Land"
						placeholder="Kies.."
						:options="sortedCountries"
					/>
				</DashboardCardCollapsible>
			</FormKit>

			<FormKit type="group" name="identification">
				<DashboardCardCollapsible heading="Legitimatie">
					<fieldset class="grid gap-2.5 xl:grid-cols-2">
						<FormKit
							type="select"
							select-icon="down"
							name="type"
							label="Legitimatiesoort"
							placeholder="Kies..."
							:options="identificationOptions"
						/>
						<FormKit name="idNumber" label="Legitimatienummer" />
					</fieldset>
					<FormKit name="issueDate" type="loanlinkDate" label="Afgiftedatum" />
					<fieldset class="grid gap-2.5 xl:grid-cols-3">
						<FormKit name="issuePlace" label="Afgifteplaats" />
						<FormKit
							name="issueCountry"
							type="select"
							select-icon="down"
							label="Afgifteland"
							placeholder="Kies..."
							:options="sortedCountries"
						/>
					</fieldset>
				</DashboardCardCollapsible>
			</FormKit>

			<FormKit type="group" name="otherDetails">
				<DashboardCardCollapsible heading="Overig">
					<fieldset class="grid gap-2.5 xl:grid-cols-3">
						<FormKit name="birthPlace" label="Geboorteplaats" />
						<FormKit
							name="birthCountry"
							type="select"
							select-icon="down"
							label="Geboorteland"
							placeholder="Kies..."
							:options="sortedCountries"
						/>
						<FormKit
							name="nationality"
							type="select"
							select-icon="down"
							label="Nationaliteit"
							placeholder="Kies..."
							:options="sortedCountries"
						/>
						<FormKit name="iban" label="Rekeningnummer (IBAN)" />
						<FormKit
							type="loanlinkOption"
							name="isFiscalResidentNL"
							label="Fiscaal inwoner van Nederland?"
							:options="yesNoOptions"
						/>
						<FormKit
							v-if="customer?.otherDetails?.isFiscalResidentNL === true"
							type="loanlinkOption"
							name="isTaxResidentAbroad"
							label="Ook fiscaal inwoner buiten Nederland?"
							:options="yesNoOptions"
						/>
						<FormKit
							v-if="customer?.otherDetails?.isTaxResidentAbroad === true"
							name="fiscalResidenceCountry"
							type="select"
							select-icon="down"
							label="Land fiscale woonplaats"
							placeholder="Kies..."
							:options="sortedCountries"
						/>
						<FormKit
							v-if="customer?.otherDetails?.isTaxResidentAbroad === true"
							name="fiscalResidenceTIN"
							label="TIN-nummer"
							placeholder="Optioneel"
						/>
						<FormKit
							type="loanlinkOption"
							name="isStarter"
							label="Eerste woning koper (starter)?"
							:options="yesNoOptions"
						/>
					</fieldset>

					<AppButton
						v-if="canImportMaritalStatus"
						variant="primary"
						class="self-start mb-4"
						type="button"
						@click="importMaritalStatus"
					>
						{{ importMaritalStatusText }}
					</AppButton>

					<InventarisationCustomerMaritalStatus />

					<fieldset class="mt-4">
						<FormKit type="group" name="formerMaritalStatus">
							<FormKit
								name="hasFormerStatus"
								type="loanlinkOption"
								label="Eerder gescheiden of een geregistreerd partnerschap ontbonden?"
								:options="yesNoOptions"
							/>

							<FormKit
								v-if="canSpecifyFormerMaritalStatus"
								name="kind"
								type="loanlinkOption"
								label="Gescheiden of geregistreerd partnerschap ontbonden?"
								:options="formerMaritalStatusOptions"
							/>

							<FormKit
								v-if="canSpecifyFormerMaritalStatus"
								name="date"
								type="loanlinkDate"
								:label="
									customer?.otherDetails?.formerMaritalStatus?.kind ===
									'03 Beide'
										? 'Wat is de datum van de meest recente ontbinding?'
										: 'Op welke datum?'
								"
							/>
						</FormKit>
					</fieldset>
				</DashboardCardCollapsible>
			</FormKit>

			<DashboardCardCollapsible v-if="SHOW_DEBUG" heading="Debug">
				<code>
					<pre class="w-full overflow-scroll">{{
						stringifyObject(customer)
					}}</pre>
				</code>
			</DashboardCardCollapsible>
		</div>
	</FormKit>
</template>

<script lang="ts" setup>
import type {
	InventarisationCustomer,
	InventarisationCustomers,
	PartialCustomer,
} from "@loanlink-nl/model-app";
import { rootClasses } from "../../../formkit.theme";
import { sortedCountries } from "@/models/inventarisation/countryOptions";
import { identificationOptions } from "@/models/inventarisation/identificationOptions";
import { computed, watch } from "vue";
import { hasAnyProperty } from "@/helpers/typeHelpers";
import { genderOptions } from "@/models/inventarisation/genderOptions";
import {
	getCustomerInitials,
	getCustomerLabel,
} from "@/helpers/inventarisation/customers";
import { isEqual } from "es-toolkit";
import { yesNoOptions } from "@/models/inventarisation/income/loondienstOptions";
import { stringifyObject } from "@/helpers/uiHelpers";

interface Props {
	customers: InventarisationCustomers;
}
const props = defineProps<Props>();

const customer = defineModel<PartialCustomer>();
const SHOW_DEBUG = import.meta.env.DEV;
const intitials = computed(() => getCustomerInitials(customer.value));

const maritalStatusImportOption = computed(() => {
	const relevantCustomers = props.customers
		.filter((c) => hasAnyProperty(c.otherDetails))
		.filter((entry) => entry.id !== customer.value?.id);

	return relevantCustomers.map((entry) => ({
		id: entry.id,
		otherDetails: entry.otherDetails,
	}))[0];
});

watch(
	() => customer?.value?.otherDetails?.formerMaritalStatus?.hasFormerStatus,
	() => {
		if (
			customer.value?.otherDetails?.formerMaritalStatus &&
			!customer.value?.otherDetails?.formerMaritalStatus?.hasFormerStatus
		) {
			customer.value.otherDetails.formerMaritalStatus = {
				hasFormerStatus: false,
			};
		}
	},
);

const canImportMaritalStatus = computed(() => {
	return (
		props.customers.length > 1 &&
		customer.value?.otherDetails?.maritalStatus?.situation !==
			maritalStatusImportOption.value?.otherDetails?.maritalStatus?.situation
	);
});

const importMaritalStatusText = computed(() => {
	const importOption =
		maritalStatusImportOption.value?.otherDetails?.maritalStatus;
	const thisCustomerLabel = customer.value?.id
		? getCustomerLabel(props.customers, customer.value?.id, true)
		: undefined;
	const otherCustomerLabel = maritalStatusImportOption.value?.id
		? getCustomerLabel(
				props.customers,
				maritalStatusImportOption.value?.id,
				true,
			)
		: undefined;

	if (importOption?.situation === "married") {
		return `${thisCustomerLabel} is getrouwd met ${otherCustomerLabel}`;
	}

	if (importOption?.situation === "partnership") {
		return `${thisCustomerLabel} heeft een registreerd partnerschap met ${otherCustomerLabel}`;
	}

	if (importOption?.situation === "coliving") {
		return `${thisCustomerLabel} woont samen met ${otherCustomerLabel}`;
	}

	return "Burgerlijke staat overnemen";
});

function importMaritalStatus() {
	if (!customer.value) {
		return;
	}

	// make a deep copy to avoid interlinked fields
	// see: https://developer.mozilla.org/en-US/docs/Glossary/Deep_copy
	const copy = JSON.parse(
		JSON.stringify(
			maritalStatusImportOption.value?.otherDetails?.maritalStatus,
		),
	);

	customer.value.otherDetails ??= {};
	customer.value.otherDetails.maritalStatus = copy;
}

const formerMaritalStatusOptions = [
	{ label: "Gescheiden", value: "01 Huwelijk" },
	{ label: "Partnerschap ontbonden", value: "02 Geregistreerd partnerschap" },
	{ label: "Beide", value: "03 Beide" },
];

/**
 * Checks if the customer has any field defined for the property.
 */
const hasResidence = (customer: InventarisationCustomer) => {
	return hasAnyProperty(customer.residence);
};

/**
 * Array of customerids that have a residence that can be imported.
 */
const residenceImportOption = computed(() => {
	const relevantCustomers = props.customers
		.filter(hasResidence)
		.filter((entry) => entry.id !== customer.value?.id);

	return relevantCustomers.map((entry) => ({
		id: entry.id,
		residence: entry.residence,
	}))[0];
});

const canSpecifyFormerMaritalStatus = computed(() => {
	return customer.value?.otherDetails?.formerMaritalStatus?.hasFormerStatus;
});

/**
 * Indicates wheter there are available residences to import.
 */
const canImportResidence = computed(() => {
	if (residenceImportOption.value?.residence) {
		const { streetName, housenumber } = residenceImportOption.value.residence;
		if (streetName && housenumber) {
			return !isEqual(
				customer.value?.residence,
				residenceImportOption.value?.residence,
			);
		}
	}

	return false;
});

const residenceImportText = computed(() => {
	if (residenceImportOption.value?.residence) {
		const { streetName, housenumber } = residenceImportOption.value.residence;
		if (streetName && housenumber) {
			return `${streetName} ${housenumber} overnemen`;
		}
	}

	return "Adres overnemen";
});

/**
 * Override the residence of current customer with available import.
 */
function importResidence() {
	if (!customer.value) {
		return;
	}

	// make a deep copy to avoid interlinked fields
	// see: https://developer.mozilla.org/en-US/docs/Glossary/Deep_copy
	const copy = JSON.parse(
		JSON.stringify(residenceImportOption.value?.residence),
	);

	// override the residence of this customer
	customer.value.residence = copy;
}

const config = {
	rootClasses,
};
</script>
